import { useState, type InputHTMLAttributes } from 'react'
import usePlacesAutocomplete, { getDetails } from 'use-places-autocomplete'

import { Input } from '@/components/primitives/Input/Input'
import { useDropdownPosition } from '@/hooks/useDropdownPosition'
import { useOnClickOutside } from '@/hooks/useOnClickOutside'
import { cn } from '@/lib/utils'

import type { CountryCode, CompanyAddressInput } from '@/types/shopify/admin/api-types'
import type { SetValue, Suggestion } from 'use-places-autocomplete'

type PlacesAutocompleteProps = InputHTMLAttributes<HTMLInputElement> & {
  region?: string
  defaultValue?: string // Enforcing defaultValue to be a string
  onInputChange?: (value: string) => void
  onPlaceSelect?: ({ placeDetails, displayAddress }: { placeDetails: CompanyAddressInput; displayAddress: string }) => void
  formValidationUI?: (props: { setValue: SetValue }) => React.ReactNode
}

export const PlacesAutocomplete = ({
  region = 'AU',
  className,
  placeholder = 'Delivery Address',
  required = false,
  disabled = false,
  defaultValue = '',
  onInputChange,
  onPlaceSelect,
  formValidationUI,
}: PlacesAutocompleteProps) => {
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    defaultValue,
    requestOptions: {
      componentRestrictions: {
        country: region,
      },
      region,
    },
    callbackName: 'placesAutocomplete',
    debounce: 300,
  })
  const [error, setError] = useState<string>()

  const { containerRef, selectionRef, dropdownPosition } = useDropdownPosition<HTMLInputElement, HTMLLIElement>(data.length, status)
  const ref = useOnClickOutside<HTMLUListElement>(() => {
    clearSuggestions()
  })

  const handleSelect = (suggestion: Suggestion) => async () => {
    setError(undefined)

    try {
      const placeDetails = await getDetails({ placeId: suggestion.place_id })

      if (!placeDetails || (typeof placeDetails !== 'string' && !placeDetails?.address_components)) {
        setError('Error getting details')

        return
      }

      const addressComponent = typeof placeDetails !== 'string' ? placeDetails.address_components : []

      const displayAddress = typeof placeDetails !== 'string' && placeDetails.formatted_address ? placeDetails.formatted_address : suggestion.description

      const streetNumber = addressComponent?.find((component: { types: string | string[] }) => component.types.includes('street_number'))?.long_name || ''
      const streetName = addressComponent?.find((component: { types: string | string[] }) => component.types.includes('route'))?.long_name || ''
      const suburb = addressComponent?.find((component: { types: string | string[] }) => component.types.includes('locality'))?.long_name || ''
      const postalCode = addressComponent?.find((component: { types: string | string[] }) => component.types.includes('postal_code'))?.short_name || ''
      const countryCode = addressComponent?.find((component: { types: string | string[] }) => component.types.includes('country'))?.short_name || ''
      const zoneCode = addressComponent?.find((component: { types: string | string[] }) => component.types.includes('administrative_area_level_1'))?.short_name || ''

      if (!streetNumber) {
        setError('Selected address must include a street number')

        return
      }

      if (!streetName) {
        setError('Selected address must include a street name')

        return
      }

      const address1 = `${streetNumber} ${streetName}`

      // External callback to set the address
      onPlaceSelect?.({
        placeDetails: {
          address1,
          city: suburb,
          countryCode: countryCode as CountryCode,
          zip: postalCode,
          zoneCode: zoneCode,
        },
        displayAddress,
      })

      clearSuggestions()

      setValue(displayAddress || suggestion.description, false) // This is the value that is displayed in the input
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error in HandleSelect in PlacesAutocomplete', e)
      clearSuggestions()
    }
  }

  return (
    <div className="h-14">
      <div className="relative z-10" ref={containerRef}>
        <Input
          value={value}
          onChange={(e) => {
            onInputChange?.(e.target.value)
            setValue(e.target.value)
            setError(undefined)
          }}
          disabled={disabled || !ready}
          error={error}
          autoComplete="address-line1"
          id="address-finder"
          className={cn('overflow-scroll border-grey-300', className)}
          placeholder={placeholder}
          type="text"
          required={required}
        />
        {status === 'OK' && (
          <ul className={cn('absolute left-0 w-full border border-neutral-400 bg-white', dropdownPosition === 'top' ? 'bottom-full' : 'top-full')} ref={ref}>
            {data.map((suggestion) => {
              const {
                place_id,
                structured_formatting: { main_text, secondary_text },
              } = suggestion

              return (
                <li className="h-12 hover:bg-neutral-100" key={place_id} ref={selectionRef}>
                  <button type="button" className="size-full px-3 py-2 text-left text-xs" onClick={handleSelect(suggestion)}>
                    {main_text} {secondary_text}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
      {formValidationUI && formValidationUI({ setValue })}
    </div>
  )
}
